// =============================================================================
// PAYMENT SUCCESS PAGE
// =============================================================================

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Mail, Clock } from 'lucide-react';
import { Button } from '@/components/Button';
import { SEOHead } from '@/components/SEO';
import { useShoppingBag } from '@/contexts/ShoppingBagContext';

export const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentDetails, setPaymentDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { clearBag } = useShoppingBag();

  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');
  const paymentMethod = searchParams.get('payment_method');

  useEffect(() => {
    const fetchSessionDetails = async () => {
      if (!sessionId && !bookingId) {
        setIsLoading(false);
        setError('No session or booking ID provided');
        return;
      }

      // Handle "other payment" booking flow
      if (bookingId && paymentMethod === 'other') {
        setPaymentDetails({
          sessionId: bookingId,
          status: 'booked',
          amount: 'To be arranged',
          service: 'Awani Clothing Order',
          email: 'Confirmed via email',
          paymentMethod: 'other'
        });
        setIsLoading(false);
        // Clear shopping bag after successful booking
        clearBag();
        return;
      }

      try {
        // Fetch real session details from Firebase function
        const functionsUrl = import.meta.env.VITE_FIREBASE_FUNCTIONS || 'http://localhost:5002';
        const response = await fetch(`${functionsUrl}/getCheckoutSession`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sessionId }),
        });

        if (!response.ok) {
          throw new Error('Failed to retrieve session details');
        }

        const sessionData = await response.json();

        // Format amount from cents to dollars
        const formattedAmount = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: sessionData.currency?.toUpperCase() || 'USD',
        }).format((sessionData.amount || 0) / 100);

        // Extract service name from metadata or line items
        const serviceName = 
          sessionData.metadata?.paymentName || 
          sessionData.lineItems?.[0]?.description || 
          'Professional Services';

        setPaymentDetails({
          sessionId: sessionData.id,
          status: sessionData.paymentStatus,
          amount: formattedAmount,
          service: serviceName,
          email: sessionData.customerEmail,
        });
        setIsLoading(false);
        // Clear shopping bag after successful payment
        clearBag();
      } catch (err) {
        // Error fetching session details
        setError(err instanceof Error ? err.message : 'Failed to load payment details');
        setIsLoading(false);
      }
    };

    fetchSessionDetails();
  }, [sessionId, bookingId, paymentMethod, clearBag]);

  if (isLoading) {
    return (
      <div className="payment-success">
        <div className="container">
          <div className="loading-state">
            <Clock size={48} className="spinning" />
            <h2>Processing Your Payment</h2>
            <p>Please wait while we confirm your payment...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-success">
        <div className="container">
          <div className="error-state">
            <h2>Unable to Load Payment Details</h2>
            <p>{error}</p>
            <p>Your payment was successful, but we couldn't retrieve the details. You should receive a confirmation email shortly.</p>
            <Button variant="primary" onClick={() => navigate('/')}>
              Return Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Payment Successful - Awani"
        description="Your payment has been processed successfully. Thank you for your business!"
        keywords={['payment success', 'thank you', 'payment confirmed'].join(', ')}
        canonicalUrl="/payment/success"
      />
      <div className="payment-success">
        <div className="container">
          <div className="success-content">
            <div className="success-icon">
              <CheckCircle size={64} />
            </div>
            
            <h1>{paymentDetails?.paymentMethod === 'other' ? 'Appointment Booked!' : 'Payment Successful!'}</h1>
            <p className="success-message">
              {paymentDetails?.paymentMethod === 'other' 
                ? 'Thank you for booking your appointment. We will contact you to arrange payment details.'
                : 'Thank you for your payment. Your transaction has been processed successfully.'
              }
            </p>

            {paymentDetails && (
              <div className="payment-details">
                <div className="detail-item">
                  <span className="label">{paymentDetails.paymentMethod === 'other' ? 'Booking ID:' : 'Transaction ID:'}</span>
                  <span className="value">{paymentDetails.sessionId}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Amount:</span>
                  <span className="value">{paymentDetails.amount}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Service:</span>
                  <span className="value">{paymentDetails.service}</span>
                </div>
                {paymentDetails.paymentMethod === 'other' && (
                  <div className="detail-item">
                    <span className="label">Payment Method:</span>
                    <span className="value">Cash, Zelle, Venmo, Wise, or PayPal (to be arranged)</span>
                  </div>
                )}
              </div>
            )}

            <div className="next-steps">
              <h3>What's Next?</h3>
              <ul>
                {paymentDetails?.paymentMethod === 'other' ? (
                  <>
                    <li>
                      <Mail size={16} />
                      <span>You'll receive a confirmation email with your appointment details</span>
                    </li>
                    <li>
                      <Clock size={16} />
                      <span>We'll contact you within 24 hours to arrange payment (Cash, Zelle, Venmo, Wise, or PayPal)</span>
                    </li>
                    <li>
                      <CheckCircle size={16} />
                      <span>Your order is confirmed and will be delivered in mid-September</span>
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      <Mail size={16} />
                      <span>You'll receive a receipt via email within the next few minutes</span>
                    </li>
                    <li>
                      <Clock size={16} />
                      <span>We'll contact you within 24 hours to confirm your order details</span>
                    </li>
                    <li>
                      <CheckCircle size={16} />
                      <span>Your order will be delivered in mid-September</span>
                    </li>
                  </>
                )}
              </ul>
            </div>

            <div className="action-buttons">
              <Button 
                variant="primary" 
                onClick={() => navigate('/')}
              >
                Return Home
                <ArrowRight size={16} />
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => navigate('/contact')}
              >
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentSuccessPage;
